import { CateringProductsTranslations } from "./catering-products-pt-br";

export const CATERING_PRODUCTS_EN_US: CateringProductsTranslations = {
	title: "Catering",
	description: "List and configure items used in catering proposals services.",
	addProduct: "Add product",
	addCombo: "Add combo",
	addMenu: {
		standalone: "Standalone / mountable",
		combo: "Combo",
		importSpreadsheet: "Import via spreadsheet",
	},
	searchPlaceholder: "Search product",
	allCategories: "All categories",
	tabs: {
		products: "Products",
		combos: "Combos",
		standalone: "Standalone",
		mountable: "Mountables",
	},
	table: {
		product: "PRODUCT",
		category: "CATEGORY",
		type: "TYPE",
		price: "PRICE",
	},
	typeLabels: {
		Product: "Standalone",
		Combo: "Combo",
		Mountable: "Mountable",
	},
	emptyState: "No products found",
	deleteConfirm: {
		title: "Delete product '{{productName}}'?",
		subtitle:
			"You are about to delete this product. This action cannot be undone. Continue?",
		subtitleWithProposals:
			"You are about to delete this product, which is <strong>linked to {{count}} active proposal(s)</strong>. This action cannot be undone. Continue?",
		confirmAction: "Delete product",
		bulkTitle: "Delete {{count}} selected products?",
		bulkSubtitle:
			"You are about to delete the selected products. This action cannot be undone. Continue?",
		bulkSubtitleWithProposals:
			"You are about to delete the selected products. The following are <strong>linked to active proposals: {{names}}</strong>. This action cannot be undone. Continue?",
		bulkConfirmAction: "Delete products",
	},
	editProposalAlert: {
		title: "Product linked to a proposal",
		subtitle:
			"This product is linked to {{count}} active proposal(s). Changes may affect the proposals in progress.",
		confirmAction: "Continue editing",
	},
	floatingBar: {
		deleteSelected: "Delete {{count}} products",
	},
	notifications: {
		priceUpdated: "Price updated successfully.",
		productRemoved: "Product removed successfully.",
		productsRemovedPartial: "{{ok}} product(s) removed, {{failed}} failed.",
		productRemoveFailed: "Could not remove the product.",
		productCreated: "Product created successfully.",
		productUpdated: "Product updated successfully.",
		comboCreated: "Combo created successfully.",
		comboUpdated: "Combo updated successfully.",
		importSuccess: "{{ok}} product(s) imported successfully.",
		importPartial: "{{ok}} product(s) imported, {{failed}} failed.",
		importFailed: "Could not import the products.",
		exportFailed: "Could not export the products.",
	},
	priceEdit: {
		validation: {
			pricePositive: "The price must be a positive value",
			maxValue: "The price must not exceed the maximum allowed value (21,474,836.47)",
		},
		changeModal: {
			title: "Change product price",
			description:
				"You are changing the price of <strong>{{productName}}</strong> to <strong>{{price}}</strong>.",
			secondParagraph:
				"Before leaving the price edition, confirm whether you want to proceed with the change or cancel.",
		},
		invalidModal: {
			title: "Invalid value",
			description:
				"The entered value is not valid. The previous product price will be kept.",
		},
	},
	categoryCombobox: {
		newCategoryWarning: "A new category will be created with the typed name.",
		createCategory: 'Create "{{name}}"',
	},
	comboForm: {
		title: "Add combo",
		editTitle: "Edit combo",
		save: "Save combo",
		details: {
			title: "Combo details",
			subtitle: "General info and pricing",
			imageAriaLabel: "Combo image",
			imageSubtitle: "Click or drag the image. Recommended size is 500 x 500 pixels.",
			fields: {
				name: { label: "Name", placeholder: "Product name" },
				category: { label: "Category", placeholder: "Beverage" },
				price: { label: "Price", placeholder: "00.00" },
				description: {
					label: "Description",
					placeholder: "Enter the combo description.",
				},
			},
		},
		products: {
			title: "Add products to the combo",
			searchPlaceholder: "Type the name of a product",
			emptyState: "No products found",
			catalogEmpty: "You must register standalone products before creating a combo.",
			selected: "{{count}} product(s) selected",
			countLabel: "Qty.",
		},
	},
	productForm: {
		tabs: {
			details: "Product details",
			mountable: "Mountable product",
		},
		mountableLocked: "To add mountable items, create a new product.",
		save: "Save product",
		sections: {
			generalInfo: {
				title: "Product details",
				subtitle: "General info and pricing",
				imageAriaLabel: "Product image",
				imageSubtitle: "Click or drag the image. Recommended size is 500 x 500 pixels.",
				fields: {
					name: {
						label: "Name",
						placeholder: "Product name",
					},
					category: {
						label: "Category",
						placeholder: "Beverage",
					},
					price: {
						label: "Price (optional)",
						placeholder: "00.00",
					},
				},
			},
			mountable: {
				title: "Mountable product",
				subtitle:
					"Mountable products let the customer combine a fixed base with a variety of add-on ingredients.",
				stepLabel: "Step",
				addStep: "Step",
				removeStep: "Remove step",
				fields: {
					title: {
						label: "Title",
						placeholder: "Enter the step title",
					},
					pricing: {
						label: "Pricing",
						placeholder: "Choose a method",
						options: {
							sum: "Sum",
							average: "Average",
							higher: "Higher",
						},
					},
					min: {
						label: "Min selectable qty for step",
						placeholder: "0",
						tooltip: "Minimum number of items to select in this step",
					},
					max: {
						label: "Max selectable qty for step",
						placeholder: "0",
						tooltip: "Maximum number of items to select in this step",
					},
				},
				items: {
					title: "Additional ingredients",
					addItem: "Add another item",
					removeItem: "Remove item",
					fields: {
						product: {
							label: "Item",
							placeholder: "Select an item",
						},
						price: {
							label: "Value",
							placeholder: "00.00",
						},
						maxCount: {
							label: "Maximum selectable quantity",
							placeholder: "Enter quantity",
						},
						storageCount: {
							label: "Stock-deducted quantity",
							placeholder: "0",
						},
					},
				},
			},
		},
		validation: {
			name: {
				required: "Enter the product name",
				min: "The name must be at least 3 characters long",
				max: "The name must be at most 100 characters long",
			},
			category: {
				required: "Select a category",
			},
			value: {
				required: "Enter the price",
				invalid: "Invalid price",
				min: "The price must be a positive value",
				max: "The price must not exceed the maximum allowed value",
			},
			combo: {
				description: {
					max: "The description must be at most 500 characters long",
				},
				products: {
					required: "Select at least 2 products",
				},
				product: {
					productId: {
						required: "Invalid product",
					},
					count: {
						min: "Quantity must be at least 1",
					},
				},
			},
			mountable: {
				sections: {
					required: "Add at least 1 section",
				},
				title: {
					required: "Enter the section title",
				},
				pricing: {
					required: "Select a pricing rule",
					invalid: "Invalid pricing rule",
				},
				max: {
					min: "The maximum must be at least 1",
					greaterThanMin: "The maximum must be greater than or equal to the minimum",
				},
				items: {
					required: "Add at least 1 item to the section",
				},
				item: {
					productId: {
						required: "Select a product",
						duplicate: "Duplicate product in the same section",
					},
					price: {
						min: "The item price must be positive",
						max: "Maximum value exceeded",
					},
					storageCount: {
						min: "Stock quantity must be at least 1",
						max: "Maximum value exceeded",
					},
				},
			},
		},
	},
};
